'use client';

/**
 * TitleBar.tsx — Voidline Electron branding strip
 *
 * Works with titleBarStyle: 'hidden' + titleBarOverlay in main.ts.
 * The OS still draws the native close/min/max buttons (styled via
 * titleBarOverlay colors). This component fills the remaining bar
 * area with the Voidline branding and acts as the window drag region.
 *
 * Renders nothing in a browser (gated on window.electronAPI).
 * Mounted in layout.tsx so it appears on every page.
 *
 * A static pre-hydration drag region div (#vl-drag-pre) in layout.tsx
 * provides window dragging before this component mounts.
 *
 * Phase J: SURFACE mode toggle lives here (Electron-only, titlebar-native).
 * When active, the full titlebar shifts to photon orange. Surface state is
 * communicated to the rest of the tree via document.body.dataset.surfaceMode
 * so any component can react without prop drilling or a context provider.
 */

import React, { useState, useEffect, useCallback } from 'react';

export function TitleBar() {
  // Defer Electron detection to after hydration to avoid SSR/client mismatch.
  const [isElectron, setIsElectron] = useState(false);
  const [surfaceActive, setSurfaceActive] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.electronAPI) {
      setIsElectron(true);
      // Re-sync surface state after a page reload — main process overlay color
      // persists across Ctrl+R but React state resets to false.
      window.electronAPI.getSurfaceMode().then(active => {
        if (active) {
          setSurfaceActive(true);
          document.body.dataset.surfaceMode = 'true';
        }
      });
    }
  }, []);

  const handleSurfaceToggle = useCallback(async () => {
    if (!window.electronAPI) return;
    const next = !surfaceActive;
    if (next) {
      await window.electronAPI.enterSurfaceMode();
      document.body.dataset.surfaceMode = 'true';
    } else {
      await window.electronAPI.exitSurfaceMode();
      delete document.body.dataset.surfaceMode;
    }
    setSurfaceActive(next);
  }, [surfaceActive]);

  // ── Hotkey bridge ────────────────────────────────────────────────────────
  // KeyboardShortcuts dispatches 'vl:surface-toggle' on Ctrl+L.
  // TitleBar owns surface state, so it listens directly.
  useEffect(() => {
    function onSurfaceToggle() { handleSurfaceToggle(); }
    window.addEventListener('vl:surface-toggle', onSurfaceToggle);
    return () => window.removeEventListener('vl:surface-toggle', onSurfaceToggle);
  }, [handleSurfaceToggle]);

  if (!isElectron) return null;

  return (
    <>
      <style>{TITLEBAR_CSS}</style>
      <div
        id="vl-titlebar"
        aria-label="Window title bar"
        data-surface={surfaceActive ? 'true' : undefined}
      >
        <img
          className="vl-tb-app-icon"
          src="/favicon.ico"
          alt=""
          width={16}
          height={16}
          draggable={false}
        />
        <span className="vl-tb-title">VOIDLINE UPLINK</span>
        <span className="vl-tb-sep">//</span>

        {/* SURFACE toggle — no-drag so clicks register */}
        <button
          type="button"
          className="vl-tb-surface-btn"
          onClick={handleSurfaceToggle}
          title={surfaceActive ? 'Unlock — return to normal window stacking' : 'Lock feed above all windows'}
          aria-pressed={surfaceActive}
        >
          {surfaceActive ? '[ UNLOCK ]' : '[ LOCK ]'}
        </button>

        <span className="vl-tb-sep">//</span>
        <span className="vl-tb-cursor" aria-hidden="true">_</span>
      </div>
    </>
  );
}

const TITLEBAR_CSS = `
#vl-titlebar {
  position: fixed;
  top: 0; left: 0; right: 0;
  height: 36px;
  z-index: 10001;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 0 16px;
  background: #000503;
  font-family: var(--vl-mono, 'Share Tech Mono', monospace);
  user-select: none;
  -webkit-user-select: none;
  overflow: hidden;
  transition: background 0.3s ease;

  -webkit-app-region: drag;
  app-region: drag;
}

/* Surface mode — full bar shifts to photon orange */
#vl-titlebar[data-surface='true'] {
  background: #1a0a00;
}

/* Hide the pre-hydration drag region once TitleBar mounts. */
#vl-drag-pre {
  display: none;
}

.vl-tb-app-icon {
  flex-shrink: 0;
  image-rendering: auto;
  opacity: 0.85;
  -webkit-app-region: no-drag;
  app-region: no-drag;
}

.vl-tb-title {
  font-size: 10px;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--vl-cyan, #4ee8d4);
  text-shadow: 0 0 8px rgba(78, 232, 212, 0.45);
  flex-shrink: 0;
  transition: color 0.3s ease, text-shadow 0.3s ease;
}
#vl-titlebar[data-surface='true'] .vl-tb-title {
  color: #f5820a;
  text-shadow: 0 0 8px rgba(245, 130, 10, 0.5);
}

.vl-tb-sep {
  font-size: 10px;
  letter-spacing: 0.12em;
  color: rgba(78, 232, 212, 0.28);
  flex-shrink: 0;
  transition: color 0.3s ease;
}
#vl-titlebar[data-surface='true'] .vl-tb-sep {
  color: rgba(245, 130, 10, 0.28);
}

.vl-tb-cursor {
  font-size: 13px;
  line-height: 1;
  color: var(--vl-cyan, #4ee8d4);
  text-shadow: 0 0 8px rgba(78, 232, 212, 0.8);
  animation: vl-tb-blink 1.2s step-end infinite;
  flex-shrink: 0;
  transition: color 0.3s ease, text-shadow 0.3s ease;
}
#vl-titlebar[data-surface='true'] .vl-tb-cursor {
  color: #f5820a;
  text-shadow: 0 0 8px rgba(245, 130, 10, 0.8);
}

@keyframes vl-tb-blink {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0; }
}

/* ── SURFACE button ─────────────────────────────────────────────────── */
.vl-tb-surface-btn {
  font-family: var(--vl-mono, 'Share Tech Mono', monospace);
  font-size: 10px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
  color: rgba(78, 232, 212, 0.4);
  text-shadow: none;
  transition: color 0.15s ease, text-shadow 0.15s ease;

  /* Must opt out of drag region so clicks register */
  -webkit-app-region: no-drag;
  app-region: no-drag;
}
.vl-tb-surface-btn:hover {
  color: rgba(78, 232, 212, 0.75);
  text-shadow: 0 0 6px rgba(78, 232, 212, 0.4);
}
#vl-titlebar[data-surface='true'] .vl-tb-surface-btn {
  color: #f5820a;
  text-shadow: 0 0 8px rgba(245, 130, 10, 0.7);
  animation: vl-surface-pulse 2s ease-in-out infinite;
}

@keyframes vl-surface-pulse {
  0%, 100% { text-shadow: 0 0 6px rgba(245, 130, 10, 0.5); }
  50%      { text-shadow: 0 0 14px rgba(245, 130, 10, 0.9), 0 0 4px rgba(255, 160, 40, 0.6); }
}

/* ── Viewport offset ──
   Push page content below the 36px titlebar. These rules only exist
   when TitleBar renders (Electron only) — browser users are unaffected.

   Homepage:  #vl-scene     (position:fixed; inset:0)
   Pre-join:  .vl-door-root (position:fixed; inset:0)
   Room:      main[data-lk-theme] wraps VoidlineRoom (position:absolute)
*/
#vl-scene,
.vl-door-root {
  top: 36px !important;
  border-top: 1px solid rgba(78, 232, 212, 0.5) !important;
}
main[data-lk-theme] {
  padding-top: 36px;
  box-sizing: border-box;
}
main[data-lk-theme] > div {
  border-top: 1px solid rgba(78, 232, 212, 0.5) !important;
}
`;
